import React, { useEffect, useRef, useState, useContext, useCallback, useMemo } from "react";
import { StatAdjuster } from "~/widgets/StatAdjuster/StatAdjuster";
import GlossaryInfoIcon from "~/widgets/GlossaryInfoIcon/GlossaryInfoIcon";
import { SettingsContext } from "~/contexts/settingsContext";
import type { GlossaryId } from "~/generated/glossary.types";
import styles from "~/pages/CharacterSheet/widgets/DerivedStatItem/DerivedStatItem.module.scss";
import { useFocus } from "~/contexts/FocusContext";
import { getDerivedStatActions } from "~/pages/CharacterSheet/characterActions";
import { AppCommand } from "~/types/commands";

export interface DerivedStatItemProps {
  label: string;
  value: number;
  max: number;
  onChange: (newValue: number) => void;
  /**
   * Size variant for the component.
   * - 'default': Full display with slider and buttons
   * - 'compact': Inline display with value/max and buttons, no slider
   */
  size?: 'default' | 'compact';
  /**
   * Optional glossary ID to show an info icon with tooltip.
   * Only shown in default size variant.
   */
  glossaryId?: GlossaryId;
}

/**
 * Get the color class based on the percentage filled.
 * - High (>66%): success/green
 * - Medium (33-66%): warning/yellow
 * - Low (<33%): danger/red
 */
function getValueColorClass(value: number, max: number): string {
  if (max === 0) return styles.valueHigh;
  const percent = value / max;
  if (percent > 0.66) return styles.valueHigh;
  if (percent > 0.33) return styles.valueMedium;
  return styles.valueLow;
}

/**
 * DerivedStatItem widget for Health Points and Resilience.
 * Includes incremental +/- buttons, a value display, and a progress bar.
 *
 * @param size - 'default' shows full layout with progress bar, 'compact' shows inline buttons only
 */
const DerivedStatItem: React.FC<DerivedStatItemProps> = ({
  label,
  value,
  max,
  onChange,
  size = 'default',
  glossaryId,
}) => {
  const settingsContext = useContext(SettingsContext);
  const animateChanges = settingsContext?.settings.display.animateStatChanges ?? false;

  const [isFlashing, setIsFlashing] = useState(false);
  const prevValueRef = useRef(value);

  // Detect value changes and trigger flash animation
  useEffect(() => {
    if (prevValueRef.current !== value && animateChanges) {
      setIsFlashing(true);
      const timer = setTimeout(() => setIsFlashing(false), 400);
      prevValueRef.current = value;
      return () => clearTimeout(timer);
    }
    prevValueRef.current = value;
  }, [value, animateChanges]);

  const handleIncrement = () => {
    onChange(Math.min(max, value + 1));
  };

  const handleDecrement = () => {
    onChange(Math.max(0, value - 1));
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;
    const newValue = Math.round(percent * max);
    onChange(Math.max(0, Math.min(max, newValue)));
  };

  const isCompact = size === 'compact';
  const isHp = label.toLowerCase().includes("hp") || label.toLowerCase().includes("health");
  const percent = max > 0 ? (value / max) * 100 : 0;
  const valueColorClass = getValueColorClass(value, max);

  const handleCommandInternal = useCallback((command: AppCommand) => {
    switch (command.type) {
      case "increase-" + label.toLowerCase():
        handleIncrement();
        break;
      case "decrease-" + label.toLowerCase():
        handleDecrement();
        break;
    }
  }, [handleIncrement, handleDecrement]);

  const { activate } = useFocus(
    "DerivedStatItem",
    useMemo(() => ({ value }), [value]),
    handleCommandInternal,
    getDerivedStatActions(label)
  );

  if (isCompact) {
    return (
      <div
        className={`${styles.compactItem} ${isFlashing ? styles.flash : ""}`}
        tabIndex={0}
        role="spinbutton"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={`${label}: ${value} of ${max}. Use up and down arrows to adjust.`}
      >
        <span className={styles.compactLabel}>{label}</span>
        <div className={styles.compactControls}>
          <button
            type="button"
            onClick={handleDecrement}
            disabled={value <= 0}
            className={styles.compactButton}
            tabIndex={-1}
            aria-hidden="true"
          >
            -
          </button>
          <span className={`${styles.compactValue} ${valueColorClass}`}>
            {value}/{max}
          </span>
          <button
            type="button"
            onClick={handleIncrement}
            disabled={value >= max}
            className={styles.compactButton}
            tabIndex={-1}
            aria-hidden="true"
          >
            +
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.derivedItem} ${isFlashing ? styles.flash : ""}`}>
      <div className={styles.derivedHeader}>
        <div className={styles.labelWithControls}>
          <span id={`${label.toLowerCase()}-label`}>
            {label}
            {glossaryId && <GlossaryInfoIcon glossaryId={glossaryId} className={styles.infoIcon} />}
          </span>
          <StatAdjuster
            value={value}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            min={0}
            label={label}
          />
        </div>
        <span className={`${styles.valueDisplay} ${valueColorClass}`} aria-live="polite">
          {value} / {max}
        </span>
      </div>
      <div
        className={`${styles.progressBar} ${isHp ? styles.progressBarHp : styles.progressBarResilience}`}
        onClick={handleProgressClick}
        role="slider"
        aria-label={`${label} slider`}
        aria-labelledby={`${label.toLowerCase()}-label`}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={`${value} out of ${max}`}
        tabIndex={0}
        onFocus={() => activate()}
      >
        <div
          className={styles.progressFill}
          style={{ width: `${percent}%` }}
        />
        {/* Segment markers */}
        <div className={styles.progressSegments}>
          {Array.from({ length: max }, (_, i) => (
            <div
              key={i}
              className={styles.progressSegment}
              style={{ left: `${((i + 1) / max) * 100}%` }}
            />
          ))}
        </div>
      </div>
    </div >
  );
};

export default DerivedStatItem;
